import { useCallback, useMemo, useRef, useState } from "react";
import { RotateCcw, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TimePicker } from "@/components/ui/time-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MINUTES_PER_DAY,
  WEEKDAY_LABELS,
  type AvailabilitySlot,
  type CurfewConfig,
  type MinuteInterval,
  type WeekdayIndex,
  availabilityFromNonCurfewWindows,
  curfewIntervalsForWeekday,
  formatAvailabilityEndToHHMM24,
  formatMinutesToHHMM,
  formatMinutesToHHMM24,
  mergeAvailabilitySlots,
  parseAvailabilityEndHHMMToMinutes,
  parseHHMMToMinutes,
  slotKey,
} from "@/lib/weekly-availability";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
/** One day column total height (header band + 24h grid). */
const COLUMN_HEIGHT_PX = 24 * 28;
/** Matches `pt-6` on the day grid — space above column cells. */
const GRID_PAD_TOP_PX = 0;
/** Matches `top-6` on gridlines layer — column header band above hour rows. */
const HEADER_BAND_PX = 24;
/** Height over which hour lines / curfew / slots are laid out (must match day column grid area). */
const HOUR_GRID_HEIGHT_PX = COLUMN_HEIGHT_PX - HEADER_BAND_PX;
/** Hour axis + day grid total height (pad + column). */
const CALENDAR_TOTAL_HEIGHT_PX = GRID_PAD_TOP_PX + COLUMN_HEIGHT_PX;
const MIN_WINDOW_MINUTES = 60;
const SNAP_MINUTES = 30;
/** Same-day availability: start ∈ [00:00, 23:30], end ∈ [00:30, 24:00) on 30-minute grid. */
const MIN_SLOT_START = 0;
const MAX_SLOT_START = 23 * 60 + 30;
const MIN_SLOT_END = 30;
const MAX_SLOT_END = MINUTES_PER_DAY;

/** Diagonal stripe overlay for site curfew (legend swatch + column blocks). */
const SITE_CURFEW_STRIPE_CLASS =
  "[background-image:repeating-linear-gradient(135deg,transparent,transparent_5px,rgb(255_255_255_/_0.18)_5px,rgb(255_255_255_/_0.18)_10px)]";

/** Same stripe pitch as site curfew, opposite diagonal (45° vs 135°). */
const AREA_CURFEW_STRIPE_CLASS =
  "[background-image:repeating-linear-gradient(45deg,transparent,transparent_5px,rgb(255_255_255_/_0.18)_5px,rgb(255_255_255_/_0.18)_10px)]";

function intervalStyle(iv: MinuteInterval): React.CSSProperties {
  const topPct = (iv.start / MINUTES_PER_DAY) * 100;
  const heightPct = ((iv.end - iv.start) / MINUTES_PER_DAY) * 100;
  return {
    top: `${topPct}%`,
    height: `${heightPct}%`,
  };
}

export interface WeeklyAvailabilityCalendarProps {
  siteCurfew?: CurfewConfig;
  areaCurfew?: CurfewConfig;
  value: AvailabilitySlot[];
  onChange: (slots: AvailabilitySlot[]) => void;
  disabled?: boolean;
  className?: string;
  /** Override the default "Use all non-curfew times" action (e.g. for light preset group editor). */
  fillAction?: { label: string; onClick: () => void };
  /**
   * Optional callback when an existing availability window is clicked (no drag).
   * Useful for opening a detail editor modal.
   */
  onSlotClick?: (slot: AvailabilitySlot) => void;
  /**
   * Prefix for derived `id`s. Root element uses this value exactly; children use `${idPrefix}-…`.
   * Use a unique prefix if multiple calendars are on one page.
   */
  idPrefix?: string;
  /**
   * Optional availability slots to use as a template for the initial state.
   */
  templateAvailability?: AvailabilitySlot[];
  /**
   * When true, template blocks and legend swatch render at 50% opacity (e.g. group
   * override on: preset schedule shown faintly behind editable windows).
   */
  dimTemplateAvailability?: boolean;
}

const DAY_ID = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

export function WeeklyAvailabilityCalendar({
  siteCurfew,
  areaCurfew,
  value,
  onChange,
  disabled = false,
  className,
  fillAction,
  onSlotClick,
  idPrefix = "weekly-availability-calendar",
  templateAvailability,
  dimTemplateAvailability = false,
}: WeeklyAvailabilityCalendarProps) {
  const [dragPreview, setDragPreview] = useState<AvailabilitySlot[] | null>(
    null,
  );
  const [hoverIntent, setHoverIntent] = useState<{
    weekday: WeekdayIndex;
    mode: "create" | "erase" | "move" | "resize-start" | "resize-end";
  } | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSlot, setEditSlot] = useState<AvailabilitySlot | null>(null);
  const [editWeekday, setEditWeekday] = useState<WeekdayIndex>(0);
  const [editStartStr, setEditStartStr] = useState("08:00");
  const [editEndStr, setEditEndStr] = useState("18:00");
  const [editError, setEditError] = useState<string | null>(null);
  const dragRef = useRef<{
    mode: "create" | "erase" | "move" | "resize-start" | "resize-end";
    weekday: WeekdayIndex;
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startMinutes: number;
    lastMinutes: number;
    originalSlot?: AvailabilitySlot;
    // Used for move: where inside the slot the grab happened.
    grabOffsetMinutes?: number;
    // For click detection
    moved: boolean;
    // snapshot of slots at drag-start
    baseSlots: AvailabilitySlot[];
  } | null>(null);

  /** Same stripe pitch as site curfew, but with purple instead of white. */
  const TEMPLATE_DISPLAY_CLASS = useMemo(() => {
    return "[background-image:repeating-linear-gradient(135deg,transparent,transparent_5px,rgb(191_143_255_/_0.18)_5px,rgb(191_143_255_/_0.18)_10px)]";
  }, []);

  const siteCfg = useMemo(
    () => ({ start: siteCurfew?.start, end: siteCurfew?.end }),
    [siteCurfew?.start, siteCurfew?.end],
  );
  const areaCfg = useMemo(
    () => ({ start: areaCurfew?.start, end: areaCurfew?.end }),
    [areaCurfew?.start, areaCurfew?.end],
  );

  const siteIntervals = useMemo(
    () => curfewIntervalsForWeekday(siteCfg),
    [siteCfg],
  );
  const areaIntervals = useMemo(
    () => curfewIntervalsForWeekday(areaCfg),
    [areaCfg],
  );

  const sortedSlots = useMemo(
    () => mergeAvailabilitySlots([...(dragPreview ?? value)]),
    [dragPreview, value],
  );

  const templateSlots = useMemo(() => {
    if (!templateAvailability?.length) return [];
    return mergeAvailabilitySlots([...templateAvailability]);
  }, [templateAvailability]);

  const fillNonCurfew = useCallback(() => {
    onChange(availabilityFromNonCurfewWindows(siteCfg, areaCfg));
  }, [onChange, siteCfg, areaCfg]);

  const clearAvailability = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const removeSlotByKey = useCallback(
    (key: string) => {
      setDragPreview(null);
      onChange(mergeAvailabilitySlots(value.filter((s) => slotKey(s) !== key)));
    },
    [onChange, value],
  );

  const snapStart = useCallback((minutes: number) => {
    const raw = Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.max(MIN_SLOT_START, Math.min(MAX_SLOT_START, raw));
  }, []);

  const snapEnd = useCallback((minutes: number) => {
    const raw = Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.max(MIN_SLOT_END, Math.min(MAX_SLOT_END, raw));
  }, []);

  /** Pointer anchor for create / move / resize (allows 24:00 for end-edge drags). */
  const snapCreateAnchor = useCallback((minutes: number) => {
    const raw = Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.max(0, Math.min(MINUTES_PER_DAY, raw));
  }, []);

  // For erase-mode we want the half-hour block you're inside (not the nearest boundary).
  const snapEraseFloor = useCallback((minutes: number) => {
    const raw = Math.floor(minutes / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.max(MIN_SLOT_START, Math.min(MAX_SLOT_START, raw));
  }, []);

  const clampedSlotFromCorners = useCallback(
    (cornerA: number, cornerB: number): { s: number; e: number } | null => {
      let s = Math.min(cornerA, cornerB);
      let e = Math.max(cornerA, cornerB);
      s = Math.max(
        MIN_SLOT_START,
        Math.min(MAX_SLOT_START, Math.round(s / SNAP_MINUTES) * SNAP_MINUTES),
      );
      e = Math.max(
        MIN_SLOT_END,
        Math.min(MAX_SLOT_END, Math.round(e / SNAP_MINUTES) * SNAP_MINUTES),
      );
      if (e - s < MIN_WINDOW_MINUTES) return null;
      return { s, e };
    },
    [],
  );

  const minutesFromPointerEvent = useCallback(
    (e: React.PointerEvent, gridEl: HTMLElement) => {
      const rect = gridEl.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const pct = y / rect.height;
      const unclamped = pct * MINUTES_PER_DAY;
      return Math.max(0, Math.min(MINUTES_PER_DAY, unclamped));
    },
    [],
  );

  const findSlotAt = useCallback(
    (daySlots: AvailabilitySlot[], minutes: number) => {
      return daySlots.find(
        (s) => minutes >= s.startMinutes && minutes <= s.endMinutes,
      );
    },
    [],
  );

  const cursorForMode = useCallback(
    (
      mode: "create" | "erase" | "move" | "resize-start" | "resize-end",
      isDragging: boolean,
    ) => {
      switch (mode) {
        case "create":
          return "cursor-crosshair";
        case "erase":
          return "cursor-no-drop";
        case "move":
          return isDragging ? "cursor-grabbing" : "cursor-grab";
        case "resize-start":
        case "resize-end":
          return "cursor-ns-resize";
        default:
          return "cursor-default";
      }
    },
    [],
  );

  const subtractInterval = useCallback(
    (
      slots: AvailabilitySlot[],
      day: WeekdayIndex,
      start: number,
      end: number,
    ) => {
      const s = Math.min(start, end);
      const e = Math.max(start, end);
      const out: AvailabilitySlot[] = [];
      for (const slot of slots) {
        if (slot.weekday !== day) {
          out.push(slot);
          continue;
        }
        if (e <= slot.startMinutes || s >= slot.endMinutes) {
          out.push(slot);
          continue;
        }
        // left remainder
        if (s > slot.startMinutes) {
          out.push({ ...slot, endMinutes: s });
        }
        // right remainder
        if (e < slot.endMinutes) {
          out.push({ ...slot, startMinutes: e });
        }
      }
      return mergeAvailabilitySlots(out);
    },
    [],
  );

  const upsertDayInterval = useCallback(
    (
      slots: AvailabilitySlot[],
      day: WeekdayIndex,
      cornerA: number,
      cornerB: number,
    ) => {
      const ce = clampedSlotFromCorners(cornerA, cornerB);
      if (!ce) return slots;
      return mergeAvailabilitySlots([
        ...slots,
        { weekday: day, startMinutes: ce.s, endMinutes: ce.e },
      ]);
    },
    [clampedSlotFromCorners],
  );

  const replaceSlot = useCallback(
    (
      slots: AvailabilitySlot[],
      from: AvailabilitySlot,
      to: AvailabilitySlot,
    ) => {
      const out = slots.filter((s) => slotKey(s) !== slotKey(from));
      out.push(to);
      return mergeAvailabilitySlots(out);
    },
    [],
  );

  const handlePointerDownOnDay = useCallback(
    (dayIndex: WeekdayIndex, e: React.PointerEvent) => {
      if (disabled) return;
      if (e.button !== 0) return;
      e.preventDefault();

      const gridEl = e.currentTarget as HTMLElement;
      const baseSlots = mergeAvailabilitySlots([...value]);
      const daySlots = baseSlots.filter((s) => s.weekday === dayIndex);

      const rawMin = minutesFromPointerEvent(e, gridEl);
      const wantsErase = e.altKey || e.metaKey || e.shiftKey;
      const snappedMin = wantsErase
        ? snapEraseFloor(rawMin)
        : snapCreateAnchor(rawMin);

      const hit = findSlotAt(daySlots, rawMin);

      let mode: "create" | "erase" | "move" | "resize-start" | "resize-end" =
        "create";
      let originalSlot: AvailabilitySlot | undefined;
      let grabOffsetMinutes: number | undefined;

      if (hit && !wantsErase) {
        originalSlot = hit;
        const handleBand = 18; // minutes near edge count as resize
        const distToStart = Math.abs(rawMin - hit.startMinutes);
        const distToEnd = Math.abs(rawMin - hit.endMinutes);
        if (distToStart <= handleBand) mode = "resize-start";
        else if (distToEnd <= handleBand) mode = "resize-end";
        else {
          mode = "move";
          grabOffsetMinutes = rawMin - hit.startMinutes;
        }
      } else if (hit && wantsErase) {
        mode = "erase";
      } else if (!hit && wantsErase) {
        mode = "erase";
      }

      dragRef.current = {
        mode,
        weekday: dayIndex,
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startMinutes: snappedMin,
        lastMinutes: snappedMin,
        originalSlot,
        grabOffsetMinutes,
        moved: false,
        baseSlots,
      };

      // Capture pointer so we keep receiving move/up even if leaving the grid.
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);

      // Prime preview for immediate feedback
      if (mode === "erase") {
        setDragPreview(
          subtractInterval(
            baseSlots,
            dayIndex,
            snappedMin,
            snappedMin + MIN_WINDOW_MINUTES,
          ),
        );
      } else if (mode === "create") {
        let iv = clampedSlotFromCorners(
          snappedMin,
          snappedMin + MIN_WINDOW_MINUTES,
        );
        if (!iv) {
          iv = clampedSlotFromCorners(
            snapStart(MAX_SLOT_END - MIN_WINDOW_MINUTES),
            MAX_SLOT_END,
          );
        }
        setDragPreview(
          iv
            ? mergeAvailabilitySlots([
                ...baseSlots,
                { weekday: dayIndex, startMinutes: iv.s, endMinutes: iv.e },
              ])
            : baseSlots,
        );
      } else {
        setDragPreview(baseSlots);
      }
    },
    [
      clampedSlotFromCorners,
      disabled,
      findSlotAt,
      minutesFromPointerEvent,
      snapCreateAnchor,
      snapEraseFloor,
      snapStart,
      subtractInterval,
      value,
    ],
  );

  const handlePointerMoveOnDay = useCallback(
    (dayIndex: WeekdayIndex, e: React.PointerEvent) => {
      const st = dragRef.current;
      if (disabled) return;
      if (!st) {
        const gridEl = e.currentTarget as HTMLElement;
        const baseSlots = mergeAvailabilitySlots([...value]);
        const daySlots = baseSlots.filter((s) => s.weekday === dayIndex);
        const rawMin = minutesFromPointerEvent(e, gridEl);
        const wantsErase = e.altKey || e.metaKey || e.shiftKey;
        const hit = findSlotAt(daySlots, rawMin);

        let mode: "create" | "erase" | "move" | "resize-start" | "resize-end" =
          "create";
        if (wantsErase) {
          mode = "erase";
        } else if (hit) {
          const handleBand = 18;
          const distToStart = Math.abs(rawMin - hit.startMinutes);
          const distToEnd = Math.abs(rawMin - hit.endMinutes);
          if (distToStart <= handleBand) mode = "resize-start";
          else if (distToEnd <= handleBand) mode = "resize-end";
          else mode = "move";
        }

        setHoverIntent({ weekday: dayIndex, mode });
        return;
      }

      if (st.weekday !== dayIndex) return;
      if (st.pointerId !== e.pointerId) return;
      e.preventDefault();

      const gridEl = e.currentTarget as HTMLElement;
      const rawMin = minutesFromPointerEvent(e, gridEl);
      const snappedMin =
        st.mode === "erase" ? snapEraseFloor(rawMin) : snapCreateAnchor(rawMin);
      if (snappedMin === st.lastMinutes) return;
      st.lastMinutes = snappedMin;
      const dx = e.clientX - st.startClientX;
      const dy = e.clientY - st.startClientY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) st.moved = true;

      const baseSlots = st.baseSlots;
      const day = st.weekday;

      if (st.mode === "create") {
        setDragPreview(
          upsertDayInterval(baseSlots, day, st.startMinutes, snappedMin),
        );
        return;
      }

      if (st.mode === "erase") {
        setDragPreview(
          subtractInterval(baseSlots, day, st.startMinutes, snappedMin),
        );
        return;
      }

      if (!st.originalSlot) return;
      const orig = st.originalSlot;

      if (st.mode === "move") {
        const duration = orig.endMinutes - orig.startMinutes;
        const grabOffset = st.grabOffsetMinutes ?? 0;
        let newStart = snapStart(snappedMin - grabOffset);
        let newEnd = newStart + duration;
        if (newEnd > MAX_SLOT_END) {
          newEnd = MAX_SLOT_END;
          newStart = snapStart(newEnd - duration);
        }
        if (newStart < MIN_SLOT_START) {
          newStart = MIN_SLOT_START;
          newEnd = snapEnd(newStart + duration);
        }
        if (newEnd > MAX_SLOT_END) newEnd = MAX_SLOT_END;
        if (newEnd - newStart < MIN_WINDOW_MINUTES) return;
        if (newStart > MAX_SLOT_START || newEnd < MIN_SLOT_END) return;
        setDragPreview(
          replaceSlot(baseSlots, orig, {
            weekday: day,
            startMinutes: newStart,
            endMinutes: newEnd,
          }),
        );
        return;
      }

      if (st.mode === "resize-start") {
        const newStart = snapStart(snappedMin);
        const end = orig.endMinutes;
        if (end - newStart < MIN_WINDOW_MINUTES) return;
        setDragPreview(
          replaceSlot(baseSlots, orig, {
            weekday: day,
            startMinutes: newStart,
            endMinutes: end,
          }),
        );
        return;
      }

      if (st.mode === "resize-end") {
        const start = orig.startMinutes;
        const newEnd = snapEnd(snappedMin);
        if (newEnd - start < MIN_WINDOW_MINUTES) return;
        setDragPreview(
          replaceSlot(baseSlots, orig, {
            weekday: day,
            startMinutes: start,
            endMinutes: newEnd,
          }),
        );
      }
    },
    [
      disabled,
      findSlotAt,
      minutesFromPointerEvent,
      replaceSlot,
      snapCreateAnchor,
      snapEnd,
      snapEraseFloor,
      snapStart,
      subtractInterval,
      upsertDayInterval,
      value,
    ],
  );

  const handlePointerUpOnDay = useCallback(
    (dayIndex: WeekdayIndex, e: React.PointerEvent) => {
      const st = dragRef.current;
      if (!st) return;
      if (st.weekday !== dayIndex) return;
      if (st.pointerId !== e.pointerId) return;
      e.preventDefault();
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);

      const preview = dragPreview;
      dragRef.current = null;

      // If it was a click (no meaningful drag) on an existing slot, surface it for a detail editor.
      if (
        !st.moved &&
        st.originalSlot &&
        (st.mode === "move" ||
          st.mode === "resize-start" ||
          st.mode === "resize-end")
      ) {
        if (onSlotClick) {
          onSlotClick(st.originalSlot);
        } else {
          setEditSlot(st.originalSlot);
          setEditWeekday(st.originalSlot.weekday);
          setEditStartStr(formatMinutesToHHMM24(st.originalSlot.startMinutes));
          setEditEndStr(
            formatAvailabilityEndToHHMM24(st.originalSlot.endMinutes),
          );
          setEditError(null);
          setEditDialogOpen(true);
        }
        setDragPreview(null);
        return;
      }

      if (preview) {
        onChange(mergeAvailabilitySlots(preview));
      }
      setDragPreview(null);
    },
    [dragPreview, onChange, onSlotClick],
  );

  const saveEditedSlot = useCallback(() => {
    if (!editSlot) return;
    const startM = parseHHMMToMinutes(editStartStr);
    const endM = parseAvailabilityEndHHMMToMinutes(editEndStr);
    if (startM == null || endM == null) {
      setEditError("Use valid 24h times (HH:MM); end may be 24:00.");
      return;
    }
    if (
      startM < MIN_SLOT_START ||
      startM > MAX_SLOT_START ||
      endM < MIN_SLOT_END ||
      endM > MAX_SLOT_END
    ) {
      setEditError(
        "Start must be between 00:00 and 23:30; end between 00:30 and 24:00.",
      );
      return;
    }
    if (startM % SNAP_MINUTES !== 0 || endM % SNAP_MINUTES !== 0) {
      setEditError("Use 30-minute steps.");
      return;
    }
    if (endM <= startM) {
      setEditError("End must be after start.");
      return;
    }
    setEditError(null);

    const next = mergeAvailabilitySlots([
      ...value.filter((s) => slotKey(s) !== slotKey(editSlot)),
      {
        weekday: editWeekday,
        startMinutes: startM,
        endMinutes: endM,
      },
    ]);
    onChange(next);
    setEditDialogOpen(false);
  }, [editEndStr, editSlot, editStartStr, editWeekday, onChange, value]);

  const deleteEditedSlot = useCallback(() => {
    if (!editSlot) return;
    onChange(value.filter((s) => slotKey(s) !== slotKey(editSlot)));
    setEditDialogOpen(false);
  }, [editSlot, onChange, value]);

  return (
    <div id={idPrefix} className={cn("space-y-4", className)}>
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit availability window</DialogTitle>
            <DialogDescription>
              Adjust day/time, or delete the window.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Day</Label>
              <Select
                value={String(editWeekday)}
                onValueChange={(v) => setEditWeekday(Number(v) as WeekdayIndex)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper">
                  {WEEKDAY_LABELS.map((d, i) => (
                    <SelectItem key={d} value={String(i)}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Start</Label>
                <TimePicker
                  value={editStartStr}
                  onChange={setEditStartStr}
                  minuteGranularity="minute"
                />
              </div>
              <div className="grid gap-1.5">
                <Label>End</Label>
                <TimePicker
                  value={editEndStr}
                  onChange={setEditEndStr}
                  minuteGranularity="minute"
                  allowEndOfDayMidnight
                />
              </div>
            </div>

            {editError && (
              <p className="text-sm text-destructive" role="alert">
                {editError}
              </p>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <Button
                type="button"
                variant="destructive"
                onClick={deleteEditedSlot}
              >
                Delete
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={saveEditedSlot}
                  disabled={!editSlot}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div
        id={`${idPrefix}-toolbar`}
        className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
      >
        <div
          id={`${idPrefix}-legend`}
          className="flex flex-wrap items-center gap-4 text-xs"
        >
          <span
            id={`${idPrefix}-legend-site`}
            className="flex items-center gap-2"
          >
            <span
              id={`${idPrefix}-legend-site-swatch`}
              className={cn(
                "inline-block size-5 rounded-sm bg-orange-600/80 ring-1 ring-orange-700/40",
                SITE_CURFEW_STRIPE_CLASS,
              )}
              aria-hidden
            />
            <span
              id={`${idPrefix}-legend-site-label`}
              className="text-muted-foreground"
            >
              Site Curfew
            </span>
          </span>
          <span
            id={`${idPrefix}-legend-area`}
            className="flex items-center gap-2"
          >
            <span
              id={`${idPrefix}-legend-area-swatch`}
              className={cn(
                "inline-block size-5 rounded-sm bg-amber-500/80 ring-1 ring-amber-600/40",
                AREA_CURFEW_STRIPE_CLASS,
              )}
              aria-hidden
            />
            <span
              id={`${idPrefix}-legend-area-label`}
              className="text-muted-foreground"
            >
              Area Curfew
            </span>
          </span>
          {templateAvailability && templateAvailability.length > 0 && (
            <span
              id={`${idPrefix}-legend-template`}
              className={cn(
                "flex items-center gap-2",
                dimTemplateAvailability && "opacity-50",
              )}
            >
              <span
                id={`${idPrefix}-legend-template-swatch`}
                className={cn(
                  "inline-block size-5 rounded-sm bg-purple-500/25 ring-1 ring-purple-500/40",
                  TEMPLATE_DISPLAY_CLASS,
                )}
                aria-hidden
              />
              <span
                id={`${idPrefix}-legend-template-label`}
                className="text-muted-foreground"
              >
                Template
              </span>
            </span>
          )}
          <span
            id={`${idPrefix}-legend-available`}
            className="flex items-center gap-2"
          >
            <span
              id={`${idPrefix}-legend-available-swatch`}
              className="inline-block size-5 rounded-sm bg-primary/80 ring-1 ring-primary/35"
              aria-hidden
            />
            <span
              id={`${idPrefix}-legend-available-label`}
              className="text-muted-foreground"
            >
              Available
            </span>
          </span>
        </div>
        <div
          id={`${idPrefix}-toolbar-actions`}
          className="flex flex-wrap gap-2"
        >
          <Button
            id={`${idPrefix}-btn-fill-non-curfew`}
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={fillAction?.onClick ?? fillNonCurfew}
            className="gap-1.5"
          >
            <RotateCcw className="size-3.5 opacity-80" />
            {fillAction?.label ?? "Use all non-curfew times"}
          </Button>
          <Button
            id={`${idPrefix}-btn-clear-availability`}
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || value.length === 0}
            onClick={clearAvailability}
          >
            Clear availability
          </Button>
        </div>
      </div>

      <p
        id={`${idPrefix}-help-curfew`}
        className="text-xs text-muted-foreground"
      >
        Curfew bands repeat every week (local time). Overnight curfews span from
        start in the evening to end the next morning, matching site and area
        settings.
      </p>

      {/* 24 x 7 grid of hours and weekdays */}
      <div
        id={`${idPrefix}-grid-scroll`}
        className="overflow-x-auto rounded-lg border border-border bg-muted/20"
      >
        <div
          id={`${idPrefix}-day-columns-header`}
          className="relative flex items-center justify-center border-b border-border bg-background py-2 text-center text-sm font-medium"
        >
          <span id={`${idPrefix}-day-columns-title`}>Available Times</span>
          <div
            id={`${idPrefix}-help-anchor`}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-primary/95 rounded-lg"
          >
            <Dialog>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button
                      id={`${idPrefix}-help-button`}
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-lg"
                      disabled={disabled}
                      aria-label="Help"
                    >
                      ?
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  id={`${idPrefix}-help-tooltip`}
                  className="bg-primary/95 text-primary-foreground shadow-lg"
                >
                  <span className="font-medium">Click</span> for help
                </TooltipContent>
              </Tooltip>
              <DialogContent
                id={`${idPrefix}-help-dialog`}
                className="sm:max-w-lg"
              >
                <DialogHeader>
                  <DialogTitle id={`${idPrefix}-help-dialog-title`}>
                    Editing availability
                  </DialogTitle>
                  <DialogDescription id={`${idPrefix}-help-dialog-desc`}>
                    Dragging on the grid snaps to the nearest half hour. The
                    detail editor lets you set start and end to any minute.
                  </DialogDescription>
                </DialogHeader>

                <div
                  id={`${idPrefix}-help-dialog-body`}
                  className="space-y-3 text-sm"
                >
                  <ul className="list-disc space-y-1 pl-5">
                    <li>
                      <span className="font-bold">Add:</span> click and drag on
                      empty space.
                    </li>
                    <li>
                      <span className="font-bold">Move:</span> click and drag
                      inside an existing window.
                    </li>
                    <li>
                      <span className="font-bold">Resize:</span> drag near the
                      top (start) or bottom (end) edge of a window.
                    </li>
                    <li>
                      <span className="font-bold">Delete:</span> click the{" "}
                      <span className="font-bold">Delete</span> button on the
                      calendar or hold <span className="font-bold">Alt</span>,{" "}
                      <span className="font-bold">Shift</span>, or{" "}
                      <span className="font-bold">Cmd</span> and drag to erase.
                    </li>
                    <li>
                      <span className="font-bold">Details:</span> click a window
                      (without dragging) to open the detailed editor.
                    </li>
                  </ul>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div
          id={`${idPrefix}-grid-inner`}
          className="flex min-w-[640px] items-stretch gap-0"
          style={{ minHeight: CALENDAR_TOTAL_HEIGHT_PX }}
        >
          <div
            id={`${idPrefix}-hour-axis`}
            className="flex shrink-0 flex-col border-r border-border bg-background/80"
            style={{ width: 40, height: CALENDAR_TOTAL_HEIGHT_PX }}
          >
            {/* <div
              id={`${idPrefix}-hour-axis-grid-pad`}
              className="shrink-0 border-b border-transparent"
              style={{ height: GRID_PAD_TOP_PX }}
              aria-hidden
            /> */}
            <div
              id={`${idPrefix}-hour-axis-header-band`}
              className="shrink-0 border-border"
              style={{ height: HEADER_BAND_PX }}
              aria-hidden
            />
            <div
              id={`${idPrefix}-hour-axis-ticks`}
              className="relative shrink-0 overflow-visible"
              style={{ height: HOUR_GRID_HEIGHT_PX }}
            >
              {HOURS.map((h) => (
                <div
                  id={`${idPrefix}-hour-label-${h}`}
                  key={h}
                  className="absolute right-1 flex justify-end text-[10px] tabular-nums leading-none text-muted-foreground"
                  style={{
                    top: `${(h / 24) * 100}%`,
                    transform: "translateY(-50%)",
                  }}
                >
                  {h.toString().padStart(2, "0")}
                </div>
              ))}
            </div>
          </div>

          <div
            id={`${idPrefix}-day-columns`}
            className="grid flex-1 grid-cols-7 gap-px bg-border"
          >
            {WEEKDAY_LABELS.map((label, dayIndex) => {
              const siteIv = siteIntervals;
              const areaIv = areaIntervals;
              const daySlots = sortedSlots.filter(
                (s) => s.weekday === dayIndex,
              );
              const dayTemplateSlots = templateSlots.filter(
                (s) => s.weekday === dayIndex,
              );
              const dayKey = DAY_ID[dayIndex];

              return (
                <div
                  id={`${idPrefix}-column-${dayKey}`}
                  key={label}
                  className="relative flex min-w-0 flex-col bg-background"
                  style={{ height: COLUMN_HEIGHT_PX }}
                >
                  <div
                    id={`${idPrefix}-column-${dayKey}-header`}
                    className="absolute left-0 right-0 top-0 z-20 border-b border-border bg-background py-1 text-center text-xs font-medium"
                  >
                    {label}
                  </div>

                  <div
                    id={`${idPrefix}-column-${dayKey}-gridlines`}
                    className="absolute left-0 right-0 bottom-0 top-6 z-0"
                    aria-hidden
                  >
                    {HOURS.map((h) => (
                      <div
                        id={`${idPrefix}-column-${dayKey}-gridline-${h}`}
                        key={h}
                        className="absolute left-0 right-0 border-t border-border/60"
                        style={{ top: `${(h / 24) * 100}%` }}
                      />
                    ))}
                  </div>

                  <div
                    id={`${idPrefix}-column-${dayKey}-curfew-layer`}
                    className="absolute left-0.5 right-0.5 top-6 bottom-0 z-[1]"
                  >
                    {siteIv.map((iv, i) => (
                      <div
                        id={`${idPrefix}-column-${dayKey}-site-curfew-${i}`}
                        key={`site-${i}`}
                        className={cn(
                          "absolute left-0 right-1/2 rounded-[2px] bg-orange-600/80",
                          SITE_CURFEW_STRIPE_CLASS,
                        )}
                        style={intervalStyle(iv)}
                        title="Site Curfew"
                      />
                    ))}
                    {areaIv.map((iv, i) => (
                      <div
                        id={`${idPrefix}-column-${dayKey}-area-curfew-${i}`}
                        key={`area-${i}`}
                        className={cn(
                          "absolute left-1/2 right-0 rounded-[2px] bg-amber-500/80",
                          AREA_CURFEW_STRIPE_CLASS,
                        )}
                        style={intervalStyle(iv)}
                        title="Area Curfew"
                      />
                    ))}
                  </div>

                  <div
                    id={`${idPrefix}-column-${dayKey}-availability-layer`}
                    className={cn(
                      "absolute left-0.5 right-0.5 top-6 bottom-0 z-[2]",
                      disabled ? "pointer-events-none" : "pointer-events-auto",
                    )}
                  >
                    {dayTemplateSlots.length > 0 && (
                      <div
                        className={cn(
                          "pointer-events-none absolute inset-0 z-[3]",
                          dimTemplateAvailability && "opacity-50",
                        )}
                      >
                        {dayTemplateSlots.map((s) => {
                          const sk = slotKey(s);
                          return (
                            <div
                              id={`${idPrefix}-column-${dayKey}-template-slot-${sk}`}
                              key={sk}
                              className={cn(
                                "pointer-events-none absolute",
                                dimTemplateAvailability
                                  ? "left-3/4 right-1"
                                  : "left-0 right-0",
                                TEMPLATE_DISPLAY_CLASS,
                              )}
                              style={intervalStyle({
                                start: s.startMinutes,
                                end: s.endMinutes,
                              })}
                              title={`Template ${formatMinutesToHHMM(s.startMinutes)}–${formatMinutesToHHMM(s.endMinutes)}`}
                            >
                              <div
                                className={cn(
                                  "pointer-events-none absolute inset-0 rounded-md bg-purple-500/50 ring-1 ring-purple-500/100 my-0.5",
                                  TEMPLATE_DISPLAY_CLASS,
                                )}
                                aria-hidden
                              />
                              {!dimTemplateAvailability && (
                                <span
                                  id={`${idPrefix}-column-${dayKey}-slot-${sk}-times`}
                                  className="pointer-events-none absolute left-2 right-2 top-1 block truncate text-left text-[10px] font-medium tabular-nums leading-none text-emerald-950 dark:text-emerald-50"
                                >
                                  {formatMinutesToHHMM(s.startMinutes)}–
                                  {formatMinutesToHHMM(s.endMinutes)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* {!disabled && (
                      <div
                        id={`${idPrefix}-column-${dayKey}-interaction-hint`}
                        className="pointer-events-none absolute left-0 right-0 top-0 z-[3] select-none text-[10px] text-muted-foreground/70"
                        style={{ transform: "translateY(-18px)" }}
                        aria-hidden
                      >
                        Drag to add • Drag window to move • Drag edges to resize
                        • Hold Alt/Shift/Cmd to erase
                      </div>
                    )} */}
                    {!disabled && (
                      <div
                        id={`${idPrefix}-column-${dayKey}-interaction-overlay`}
                        className={cn(
                          "absolute inset-0 z-[4]",
                          cursorForMode(
                            dragRef.current?.weekday ===
                              (dayIndex as WeekdayIndex)
                              ? dragRef.current.mode
                              : hoverIntent?.weekday ===
                                  (dayIndex as WeekdayIndex)
                                ? hoverIntent.mode
                                : "create",
                            dragRef.current?.weekday ===
                              (dayIndex as WeekdayIndex),
                          ),
                        )}
                        onPointerDown={(e) =>
                          handlePointerDownOnDay(dayIndex as WeekdayIndex, e)
                        }
                        onPointerMove={(e) =>
                          handlePointerMoveOnDay(dayIndex as WeekdayIndex, e)
                        }
                        onPointerUp={(e) =>
                          handlePointerUpOnDay(dayIndex as WeekdayIndex, e)
                        }
                        onPointerLeave={() => setHoverIntent(null)}
                      />
                    )}
                    {daySlots.map((s) => {
                      const sk = slotKey(s);
                      return (
                        <div
                          id={`${idPrefix}-column-${dayKey}-slot-${sk}`}
                          key={sk}
                          className="pointer-events-none absolute left-0 right-0 z-[5]"
                          style={intervalStyle({
                            start: s.startMinutes,
                            end: s.endMinutes,
                          })}
                          title={`Available ${formatMinutesToHHMM(s.startMinutes)}–${formatMinutesToHHMM(s.endMinutes)}`}
                        >
                          <div
                            className="pointer-events-none absolute inset-0 rounded-md bg-primary/80 ring-1 ring-primary/100 ml-1 mr-4 my-0.5"
                            aria-hidden
                          />
                          <span
                            id={`${idPrefix}-column-${dayKey}-slot-${sk}-times`}
                            className="pointer-events-none absolute left-2 right-2 top-1 block truncate text-left text-[10px] font-medium tabular-nums leading-none text-emerald-950 dark:text-emerald-50"
                          >
                            {formatMinutesToHHMM(s.startMinutes)}–
                            {formatMinutesToHHMM(s.endMinutes)}
                          </span>
                          {!disabled && (
                            <Button
                              id={`${idPrefix}-column-${dayKey}-slot-${sk}-delete`}
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute bottom-1 right-5 z-[1] h-5 w-5 p-0 bg-destructive text-white hover:bg-destructive hover:text-white focus-visible:bg-destructive focus-visible:text-white active:bg-destructive active:text-white pointer-events-auto [&_svg]:size-4 [&_svg]:text-white"
                              aria-label={`Delete ${WEEKDAY_LABELS[s.weekday]} ${formatMinutesToHHMM(s.startMinutes)}–${formatMinutesToHHMM(s.endMinutes)}`}
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSlotByKey(sk);
                              }}
                            >
                              <Trash2 className="text-current" aria-hidden />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
